/**
 * EditorialSubsection — a THEME-ADAPTIVE content sub-block. The chrome
 * switches by `theme.headingScale` (+ `theme.density`) so each preset
 * family reads as a structurally DIFFERENT design system, not the same
 * boxy card recoloured:
 *
 *   - editorial (Journal / Quarterly / Monograph): NO box. A journal-
 *     article flow — a hairline TOP rule, generous whitespace, a
 *     small-caps mono label run above flowing prose. (`.subsec` in
 *     docs/design/journal-section-layouts.html:
 *       border-top: 1px solid var(--hair-strong) ← roleColor(theme,'rule')
 *       label: mono, 11px, .14em tracking, uppercase.)
 *   - technical (Institutional): a LIGHT structured frame — a thin LEFT
 *     accent rule + subtle inset, crisp but not a heavy card.
 *   - compact (Review / Proceedings — IEEE/ACM density): DENSE, minimal
 *     chrome — a tight thin top hairline, the smallest label/body type,
 *     no padding bloat.
 *
 * `theme.density === 'compact'` tightens the spacing one extra notch on
 * any scale.
 *
 * Theme-aware: every rule/ink reads `roleColor(theme, ...)`. No literal
 * slate/blue/green. Reduced-motion / print friendly (no animation, no
 * shadow). The DOM shape (root → label div → body div), the
 * accentLabel + children API, the optional id/className/style, and ALL
 * call-site contracts are UNCHANGED — the label stays the root's first
 * child on every scale.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { reportShellBodyFontSize, REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

export interface EditorialSubsectionProps {
  theme: ReportTheme;
  /** Small-caps mono label rendered above the hairline rule's content. */
  label: React.ReactNode;
  children: React.ReactNode;
  /** Optional id for anchoring / section ordering. */
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Use accent ink for the label instead of heading ink (default ink). */
  accentLabel?: boolean;
  /**
   * N1 (Editorial Report Redesign v2.0) — when true, stamp `data-editorial-lead`
   * on the body block so the editorial drop-cap (`[data-editorial-lead]::first-
   * letter`, scoped under `[data-report-root][data-editorial]`) renders a raised
   * serif initial on this LEAD prose block. Additive + scoped: the cap only
   * fires when the editorial flag is on AND a renderer deliberately marks ONE
   * lead sub-section per section (never every paragraph). Default false (no cap).
   */
  leadCap?: boolean;
}

/** Resolve to a known scale, defaulting unknown/legacy values to editorial
 *  (mirrors resolveReportHeadingScale in SiteRagReportView). */
function resolveScale(
  scale: ReportTheme['headingScale'] | undefined,
): ReportTheme['headingScale'] {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

import { useSectionShellContext } from '../../sections/SectionShell';
import { hasGeneratedEditorialLayer } from '../../../../lib/editorial/resolveEditorialLayer';
import { partitionHeadBodyLead } from '../../../../lib/reportRendering/layoutGroups';

export function EditorialSubsection({
  theme,
  label,
  children,
  id,
  className,
  style,
  accentLabel = false,
  leadCap = false,
}: EditorialSubsectionProps): React.ReactElement | null {
  const { report, sectionId, isEditorialActive } = useSectionShellContext();
  if (report && sectionId && isEditorialActive && hasGeneratedEditorialLayer(report, sectionId)) {
    return null;
  }

  const scale = resolveScale(theme.headingScale);
  const dense = theme.density === 'compact';
  const labelInk = roleColor(theme, accentLabel ? 'accent' : 'ink');

  // ── Per-scale chrome ──────────────────────────────────────────────
  // editorial → hairline TOP rule + generous whitespace (journal flow).
  // technical → thin LEFT accent rule + subtle inset (light frame).
  // compact   → tight thin TOP hairline, minimal padding (IEEE density).
  let rootStyle: React.CSSProperties;
  let labelStyle: React.CSSProperties;
  let bodyStyle: React.CSSProperties;

  if (scale === 'technical') {
    // William QA 2026-06-28 — left accent rule removed report-wide
    rootStyle = {
      paddingTop: dense ? 2 : 3,
      paddingBottom: dense ? 2 : 3,
    };
    labelStyle = {
      fontFamily: MONO,
      // 12px section-label token (text-xs) — unified across editorial labels.
      fontSize: REPORT_TEXT_PX.label,
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: labelInk,
      marginBottom: dense ? 4 : 5,
    };
    bodyStyle = {
      color: roleColor(theme, 'body'),
      // Unified report BODY size via the type scale (≈12.6px = 9.5pt, densified
      // 2026-06-27) — matches prose + table cells.
      fontSize: reportShellBodyFontSize(theme),
      lineHeight: 1.55,
    };
  } else if (scale === 'compact') {
    rootStyle = {
      borderTop: `1px solid ${roleColor(theme, 'hair')}`,
      paddingTop: dense ? 5 : 6,
    };
    labelStyle = {
      fontFamily: MONO,
      // 12px section-label token (text-xs) — unified across editorial labels.
      fontSize: REPORT_TEXT_PX.label,
      fontWeight: 600,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: labelInk,
      marginBottom: dense ? 2 : 3,
    };
    bodyStyle = {
      color: roleColor(theme, 'body'),
      // Unified report BODY size via the type scale (≈12.6px = 9.5pt, densified
      // 2026-06-27) — matches prose + table cells.
      fontSize: reportShellBodyFontSize(theme),
      lineHeight: 1.5,
    };
  } else {
    // editorial — journal-article flow: hairline TOP rule + generous lead.
    rootStyle = {
      borderTop: `1px solid ${roleColor(theme, 'rule')}`,
      paddingTop: dense ? 10 : 14,
    };
    labelStyle = {
      fontFamily: MONO,
      // 12px section-label token (text-xs) — unified across editorial labels.
      fontSize: REPORT_TEXT_PX.label,
      fontWeight: 600,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: labelInk,
      marginBottom: dense ? 6 : 8,
    };
    bodyStyle = {
      color: roleColor(theme, 'body'),
      // Unified report BODY size via the type scale (≈12.6px = 9.5pt, densified
      // 2026-06-27) — matches prose + table cells.
      fontSize: reportShellBodyFontSize(theme),
      lineHeight: 1.6,
    };
  }

  // ── P-GROUP Phase A (`head-body`) — bind the small-caps sub-head to its
  // FIRST body block so the label can never strand alone at a page foot (the
  // live RAIL ACCESS / TRANSMISSION ACCESS / RECOMMENDATIONS class: pagedjs
  // broke between this label div and the body div — the label is NOT a
  // [data-heading], so no existing keep rule or post-pass ever covered it).
  // `partitionHeadBodyLead` is conservative: it pairs the label with either the
  // whole inline prose run, the FIRST block child (rest flows freely), or a
  // lone paragraph — and declines to pair an unbounded lone container (an
  // avoid-inside box taller than a page CLIPS in pagedjs, which is worse than
  // the strand it would prevent). When it declines, the DOM is exactly the
  // pre-P-GROUP shape.
  const { lead, rest } = partitionHeadBodyLead(children);
  const labelNode = (
    <div style={{ ...labelStyle, breakAfter: 'avoid', pageBreakAfter: 'avoid' }}>
      {label}
    </div>
  );
  const bodyProps = leadCap ? { 'data-editorial-lead': 'true' as const } : {};

  if (lead.length === 0) {
    return (
      <div id={id} className={className} style={{ ...rootStyle, ...style }}>
        {labelNode}
        <div style={{ ...bodyStyle, breakBefore: 'avoid', pageBreakBefore: 'avoid' }} {...bodyProps}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div id={id} className={className} style={{ ...rootStyle, ...style }}>
      <div data-layout-group="head-body" style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
        {labelNode}
        <div style={{ ...bodyStyle, breakBefore: 'avoid', pageBreakBefore: 'avoid' }} {...bodyProps}>
          {lead}
        </div>
      </div>
      {rest.length > 0 && <div style={bodyStyle}>{rest}</div>}
    </div>
  );
}
